import React, { useEffect } from 'react'

interface PulsatingOrbProps {
  size?: 'sm' | 'md' | 'lg'
  variant?: 'orange' | 'white'
  className?: string
}

// Inject keyframes into document head once
let keyframesInjected = false

/**
 * Double-ring breathing animation for AI "thinking" states
 * Two rings represent SyntaxLens's layered learning approach
 * Provides a calm, organic loading indicator that's less mechanical than spinners
 */
export const PulsatingOrb: React.FC<PulsatingOrbProps> = ({
  size = 'sm',
  variant = 'orange',
  className = ''
}) => {
  const sizeMap = {
    sm: 16,
    md: 24,
    lg: 32
  }

  const dimension = sizeMap[size]

  // Inject keyframes into document head on mount
  useEffect(() => {
    if (!keyframesInjected) {
      const style = document.createElement('style')
      style.id = 'pulsating-orb-keyframes'
      style.textContent = `
        @keyframes breatheRing1 {
          0%, 100% {
            transform: scale(1);
            opacity: 0.85;
          }
          50% {
            transform: scale(1.15);
            opacity: 0.65;
          }
        }
        @keyframes breatheRing2 {
          0%, 100% {
            transform: scale(1);
            opacity: 0.4;
          }
          50% {
            transform: scale(1.2);
            opacity: 0.2;
          }
        }
      `
      document.head.appendChild(style)
      keyframesInjected = true
    }
  }, [])

  // Calculate dimensions for 2 concentric rings
  const borderThickness = size === 'sm' ? 1.5 : size === 'md' ? 2 : 2.5
  const ring1Size = dimension * 0.5   // Inner ring (stronger)
  const ring2Size = dimension          // Outer ring (subtle)

  const color = variant === 'white' ? 'rgba(255, 255, 255, 0.9)' : '#f97316'

  const containerStyle = {
    position: 'relative' as const,
    width: `${dimension}px`,
    height: `${dimension}px`,
  }

  const ringBaseStyle = {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    borderRadius: '50%',
    border: `${borderThickness}px solid ${color}`,
    background: 'transparent',
  }

  const ring1Style = {
    ...ringBaseStyle,
    width: `${ring1Size}px`,
    height: `${ring1Size}px`,
    marginLeft: `-${ring1Size / 2}px`,
    marginTop: `-${ring1Size / 2}px`,
    animation: 'breatheRing1 2s ease-in-out infinite',
  }

  const ring2Style = {
    ...ringBaseStyle,
    width: `${ring2Size}px`,
    height: `${ring2Size}px`,
    marginLeft: `-${ring2Size / 2}px`,
    marginTop: `-${ring2Size / 2}px`,
    animation: 'breatheRing2 2s ease-in-out infinite 0.3s', // 0.3s delay
  }

  return (
    <div
      className={className}
      style={containerStyle}
      aria-label="AI analyzing code"
      title="Layered learning in progress"
    >
      <div style={ring1Style} />
      <div style={ring2Style} />
    </div>
  )
}

export default PulsatingOrb
